/**
 * Registrar Factura General CXP — migración web del modo "General" de ares8510.
 *
 * Registra una factura de suplidor sin motores en cuentas por pagar (tabla
 * cxp): suplidor, número de factura, valor, tasa, tipo de comprobante y
 * concepto. El registro de facturas con motores (modo "Con motores" del
 * escritorio) lo cubre Inventario → Compra de inventario (bulk).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { imprimirFacturaCxp } from "modules/cuentasxpagar/impresionfacturacxp";

const filtrarSuplidores = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.id} ${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color, extra }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
      {extra}
    </Box>
  );
}

export default function FacturaGeneralCxp({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [suplidores, setSuplidores] = useState([]);
  const [tiposComprobante, setTiposComprobante] = useState([]);
  const [compania, setCompania] = useState(null);
  const [cargando, setCargando] = useState(true);

  const [suplidor, setSuplidor] = useState(null);
  const [nfactura, setNfactura] = useState("");
  const [valor, setValor] = useState("");
  const [tasa, setTasa] = useState("");
  const [tipocomprobante, setTipocomprobante] = useState("");
  const [concepto, setConcepto] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const [supl, conf] = await Promise.all([
        post("/api/cxp_suplidores", {}),
        post("/api/cxp_config", {}),
      ]);
      if (supl.ok) setSuplidores(supl.data.suplidores || []);
      else
        showAlert({
          message: supl.data.Error || "No se pudieron listar los suplidores",
          color: "error",
        });
      if (conf.ok) {
        setTiposComprobante(conf.data.tiposComprobante || []);
        setCompania(conf.data.compania || null);
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setSuplidor(null);
    setNfactura("");
    setValor("");
    setTasa("");
    setTipocomprobante("");
    setConcepto("");
  };

  const guardar = async () => {
    if (!suplidor) {
      showAlert({ message: "Debe seleccionar el suplidor", color: "warning" });
      return;
    }
    if (!nfactura.trim()) {
      showAlert({ message: "El número de factura no puede estar en blanco", color: "warning" });
      return;
    }
    if (toNum(valor) <= 0) {
      showAlert({ message: "El valor no puede ser 0", color: "warning" });
      return;
    }
    if (!concepto.trim()) {
      showAlert({ message: "El concepto no puede estar en blanco", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cxp_factura_general", {
        suplidor: suplidor.id,
        nfactura: nfactura.trim(),
        valor: toNum(valor),
        tasa: toNum(tasa),
        tipocomprobante,
        concepto: concepto.trim(),
      });
      if (ok) {
        showAlert({
          message: data.ok || `Factura ${data.nfactura || nfactura} registrada`,
          color: "success",
        });
        imprimirFacturaCxp({
          compania,
          factura: {
            nfactura: data.nfactura || nfactura.trim(),
            fecha: new Date().toLocaleDateString("es-DO"),
            suplidornombre: suplidor.descrip,
            suplidorrnc: suplidor.rnc,
            tipofactura: "General",
            tipocomprobante,
            concepto: concepto.trim(),
            valor: toNum(valor),
          },
          lineas: [],
        });
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la factura", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registrar Factura General CXP"
        subtitle="Facturas de suplidores sin motores en cuentas por pagar"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Suplidor */}
        <Box sx={sectionBox}>
          <SectionTitle icon={StorefrontOutlinedIcon} color={FP.color.accentBlue}>
            Suplidor
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarSuplidores}
                options={suplidores}
                loading={cargando}
                value={suplidor}
                getOptionLabel={(o) => (typeof o === "string" ? o : `${o.id} — ${o.descrip || ""}`)}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => setSuplidor(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.id} — {o.descrip}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.rnc && `RNC ${o.rnc}`, o.telefono, o.ciudad]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Suplidor" sx={fpInputSx} />}
              />
            </Grid>
            {suplidor && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <Chip
                  label={[suplidor.rnc && `RNC ${suplidor.rnc}`, suplidor.ciudad]
                    .filter(Boolean)
                    .join(" · ")}
                  sx={{
                    bgcolor: FP.color.surfaceAlt,
                    color: FP.color.textSecondary,
                    fontWeight: 700,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Factura */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={ReceiptLongOutlinedIcon} color={FP.color.navy}>
            Datos de la Factura
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="# Factura"
                value={nfactura}
                onChange={(e) => setNfactura(e.target.value.replace(/\D/g, ""))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Valor"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="0.00"
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Tasa"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                placeholder="0.00"
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo de Comprobante"
                value={tipocomprobante}
                onChange={(e) => setTipocomprobante(e.target.value)}
                disabled={cargando}
                sx={fpInputSx}
              >
                {tiposComprobante.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8} sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                label="Las facturas con motores se registran desde Inventario → Compra de inventario (bulk)"
                sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={guardar}
                disabled={guardando || cargando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
